class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.onCheckboxChange = this.onCheckboxChange.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    
    // Track if we're handling an unchecked checkbox to prevent double submission
    this.isHandlingUncheck = false;
    
    facetForm.addEventListener('input', (event) => {
      // Skip if we're handling an unchecked checkbox (it will use removal URL)
      if (this.isHandlingUncheck) {
        this.isHandlingUncheck = false;
        return;
      }
      this.debouncedOnSubmit(event);
    });
    
    // Listen for checkbox changes to handle unchecked checkboxes
    facetForm.addEventListener('change', (event) => {
      if (event.target.type === 'checkbox') {
        this.onCheckboxChange(event);
      }
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }
  
  onCheckboxChange(event) {
    const checkbox = event.target;
    
    // If checkbox is being unchecked and has a removal URL, use it directly
    if (!checkbox.checked && checkbox.dataset.urlToRemove) {
      // Set flag to prevent input event from processing
      this.isHandlingUncheck = true;
      
      // Use the removal URL which already has the correct state
      const removalUrl = checkbox.dataset.urlToRemove;
      const searchParams = removalUrl.indexOf('?') === -1 
        ? '' 
        : removalUrl.slice(removalUrl.indexOf('?') + 1);
      
      // Merge with sort_by from current form if it exists
      const form = checkbox.closest('form');
      const sortSelect = form.querySelector('select[name="sort_by"]');
      if (sortSelect && sortSelect.value) {
        const params = new URLSearchParams(searchParams);
        params.set('sort_by', sortSelect.value);
        this.onSubmitForm(params.toString(), event);
      } else {
        this.onSubmitForm(searchParams, event);
      }
      
      return false;
    }
    
    // For checked checkboxes, let the normal input handler process it
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    FacetFiltersForm.bindPaginationEvents();
  }

  static renderProductCount(html) {
    const normalizeSearchCount = (value) =>
      value
        .trim()
        .replaceAll('&quot;', '"')
        .replaceAll('&#34;', '"')
        .replaceAll('&amp;quot;', '"')
        .replaceAll('&amp;#34;', '"')
        .replaceAll('&#1;', "'")
        .replaceAll('&#39;', "'")
        .replaceAll('&amp;#1;', "'")
        .replaceAll('&amp;#39;', "'");
    const count = normalizeSearchCount(
      new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML
    );
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static bindPaginationEvents() {
    const paginationLinks = document.querySelectorAll('#ProductGridContainer .pagination a');
    paginationLinks.forEach((link) => {
      if (link.dataset.paginationBound === 'true') return;
      link.dataset.paginationBound = 'true';

      link.addEventListener('click', (event) => {
        event.preventDefault();
        const url = new URL(link.href, window.location.origin);
        FacetFiltersForm.renderPage(url.searchParams.toString(), event);
      });
    });
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const href = event.currentTarget.href || event.currentTarget.dataset.href || '';
    const url = href.indexOf('?') == -1 ? '' : href.slice(href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.bindPaginationEvents();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a, button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    // Anchors used as buttons need an explicit role and manual Space activation;
    // real <button> elements get both natively, so only patch anchors.
    if (facetLink.tagName === 'A') {
      facetLink.setAttribute('role', 'button');
      facetLink.addEventListener('keyup', (event) => {
        event.preventDefault();
        if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
      });
    }
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

// Custom sort dropdown handler
(function() {
  let initializedDropdowns = new WeakSet();
  
  function initCustomSortDropdowns() {
    document.querySelectorAll('.custom-sort-dropdown').forEach((dropdown) => {
      // Skip if already initialized
      if (initializedDropdowns.has(dropdown)) return;
      initializedDropdowns.add(dropdown);
      
      const form = dropdown.closest('form');
      if (!form) return;
      
      const hiddenInput = form.querySelector('input[name="sort_by"]');
      const label = dropdown.querySelector('.custom-sort-label');
      const options = dropdown.querySelectorAll('.custom-sort-option');
      
      options.forEach((option) => {
        const input = option.querySelector('.custom-sort-input');
        const optionLabel = option.dataset.label;
        const optionValue = option.dataset.value;
        
        // Handle label click
        option.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          // Only proceed if this option is not already selected
          if (input && input.checked) {
            return;
          }
          
          // Update hidden input
          if (hiddenInput) {
            hiddenInput.value = optionValue;
          }
          
          // Update label
          if (label) {
            label.textContent = optionLabel;
          }
          console.log(optionLabel);
          
          // Update active state - remove from all, add to current
          options.forEach((opt) => opt.classList.remove('active'));
          option.classList.add('active');
          
          // Update radio buttons - browser will handle single-select via name attribute
          // but we ensure visual state is correct
          options.forEach((opt) => {
            const radio = opt.querySelector('.custom-sort-input');
            if (radio) radio.checked = false;
          });
          if (input) input.checked = true;
          
          // Close dropdown
          dropdown.removeAttribute('open');
          
          // Submit form
          const facetForm = form.closest('facet-filters-form');
          if (facetForm && facetForm.debouncedOnSubmit) {
            // Create a fake event to trigger form submission
            const fakeEvent = new Event('input', { bubbles: true });
            form.dispatchEvent(fakeEvent);
          }
        });
        
        // Handle radio button change event (for keyboard navigation and direct clicks)
        if (input) {
          input.addEventListener('change', (e) => {
            // Update hidden input
            if (hiddenInput) {
              hiddenInput.value = optionValue;
            }
            
            // Update label
            if (label) {
              label.textContent = optionLabel;
            }
            
            // Update active state
            options.forEach((opt) => opt.classList.remove('active'));
            if (input.checked) {
              option.classList.add('active');
            }
            
            // Close dropdown
            dropdown.removeAttribute('open');
            
            // Submit form
            const facetForm = form.closest('facet-filters-form');
            if (facetForm && facetForm.debouncedOnSubmit) {
              const fakeEvent = new Event('input', { bubbles: true });
              form.dispatchEvent(fakeEvent);
            }
          });
        }
      });
    });
  }
  
  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCustomSortDropdowns);
  } else {
    initCustomSortDropdowns();
  }
  
  // Function to sync sort dropdown state from the DOM
  function syncSortDropdownState() {
    document.querySelectorAll('.custom-sort-dropdown').forEach((dropdown) => {
      const form = dropdown.closest('form');
      if (!form) return;
      
      const hiddenInput = form.querySelector('input[name="sort_by"]');
      const label = dropdown.querySelector('.custom-sort-label');
      const options = dropdown.querySelectorAll('.custom-sort-option');
      
      if (!hiddenInput || !label) return;
      
      const currentValue = hiddenInput.value;
      let selectedLabel = null;
      
      // Update active state and find selected label
      options.forEach((option) => {
        const input = option.querySelector('.custom-sort-input');
        const optionValue = option.dataset.value;
        const optionLabel = option.dataset.label;
        
        if (currentValue && optionValue === currentValue) {
          // This option should be selected
          option.classList.add('active');
          if (input) input.checked = true;
          selectedLabel = optionLabel;
        } else {
          // This option should not be selected
          option.classList.remove('active');
          if (input) input.checked = false;
        }
      });
      
      // Update label
      if (selectedLabel) {
        label.textContent = selectedLabel;
      } else {
        // Show blank when no option is selected
        label.textContent = '';
      }
    });
  }
  
  // Re-initialize after AJAX updates (for dynamic content)
  const originalRenderPage = FacetFiltersForm.renderPage;
  FacetFiltersForm.renderPage = function(searchParams, event, updateURLHash) {
    const result = originalRenderPage.call(this, searchParams, event, updateURLHash);
    setTimeout(() => {
      initCustomSortDropdowns();
      syncSortDropdownState();
    }, 100);
    return result;
  };
  
  // Immediately update aria-label on click before AJAX completes
  document.addEventListener('change', function(event) {
    var input = event.target;
    if (input.type !== 'checkbox' || !input.closest('.facet-checkbox') || !input.hasAttribute('aria-label')) return;
    var form = input.closest('form[data-filter-checked-text]');
    var checkedText = form ? form.dataset.filterCheckedText : '';
    var uncheckedText = form ? form.dataset.filterUncheckedText : '';
    var base = input.dataset.baseLabel;
    if (!base) {
      base = input.getAttribute('aria-label').replace(new RegExp(',\\s*' + checkedText + '$'), '');
      input.dataset.baseLabel = base;
    }
    input.setAttribute('aria-label', base + (input.checked ? ', ' + checkedText : (uncheckedText ? ', ' + uncheckedText : '')));
  });

  // Also sync after renderFilters and renderAdditionalElements complete
  const originalRenderFilters = FacetFiltersForm.renderFilters;
  FacetFiltersForm.renderFilters = function(html, event) {
    const result = originalRenderFilters.call(this, html, event);
    setTimeout(() => {
      syncSortDropdownState();
      initCustomSortDropdowns();
    }, 50);
    return result;
  };

  const originalRenderAdditionalElements = FacetFiltersForm.renderAdditionalElements;
  FacetFiltersForm.renderAdditionalElements = function(html) {
    const result = originalRenderAdditionalElements.call(this, html);
    setTimeout(() => {
      syncSortDropdownState();
      initCustomSortDropdowns();
    }, 50);
    return result;
  };
})();
